import React, { useState, useMemo } from 'react';
import { Building2, TrendingUp, Users, Calendar, ArrowRight, Search } from 'lucide-react';
import { sans, mono, green, greenLight, amber, red, useTheme, useCurrency } from '../../tokens';
import { fmtCompactC } from '../../helpers';
import { MOCK_PORTFOLIO } from '../../data/mockPortfolio';

// Investor landing page that lists every company the current investor
// has access to. Clicking a card drills into that company's full
// investor report.
//
// Sort options: name, cash, runway, last published
// Filter: free-text search
// Status handling: cards with status='not_published' render "Data available soon".

const SORTS = [
  { key: 'name',   label: 'Name' },
  { key: 'cash',   label: 'Cash' },
  { key: 'runway', label: 'Runway' },
  { key: 'recent', label: 'Most recent' },
];

function runwayColor(months) {
  if (months == null) return null;
  if (months < 12) return red;
  if (months < 18) return amber;
  return green;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PortfolioOverview({ onOpenCompany, companies = MOCK_PORTFOLIO }) {
  const t = useTheme();
  const cc = useCurrency();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('recent');

  const filtered = useMemo(() => {
    let list = [...companies];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.industry || '').toLowerCase().includes(q) ||
        (c.hq || '').toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      if (sortKey === 'name')   return a.name.localeCompare(b.name);
      if (sortKey === 'cash')   return (b.cashCad || 0) - (a.cashCad || 0);
      if (sortKey === 'runway') return (b.runwayMonths || 0) - (a.runwayMonths || 0);
      // recent: unpublished last, otherwise newest publishedAt first
      if (!a.lastPublishedAt && !b.lastPublishedAt) return 0;
      if (!a.lastPublishedAt) return 1;
      if (!b.lastPublishedAt) return -1;
      return new Date(b.lastPublishedAt) - new Date(a.lastPublishedAt);
    });
    return list;
  }, [companies, search, sortKey]);

  return (
    <div style={{ minHeight: '100vh', background: t.bg, padding: '32px 24px 60px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: sans, color: t.textPrimary }}>
            Portfolio Overview
          </div>
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, marginTop: 4 }}>
            {companies.length} {companies.length === 1 ? 'company' : 'companies'} in your portfolio
          </div>
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 8,
            background: t.bgCard, border: `1px solid ${t.border}`,
            flex: '1 1 260px', minWidth: 240, maxWidth: 360,
          }}>
            <Search size={13} style={{ color: t.textMuted }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search companies…"
              style={{
                border: 'none', outline: 'none', background: 'transparent',
                fontSize: 12, fontFamily: sans, color: t.textPrimary, width: '100%',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {SORTS.map(s => (
              <button
                key={s.key}
                onClick={() => setSortKey(s.key)}
                style={{
                  padding: '6px 12px', borderRadius: 16,
                  border: `1px solid ${sortKey === s.key ? green : t.border}`,
                  background: sortKey === s.key ? greenLight : t.bgCard,
                  color: sortKey === s.key ? green : t.textMuted,
                  fontSize: 10, fontWeight: 600, fontFamily: sans, cursor: 'pointer',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 16,
        }}>
          {filtered.map(c => {
            const published = c.status === 'published';
            const rColor = runwayColor(c.runwayMonths);
            return (
              <div
                key={c.id}
                onClick={() => onOpenCompany && onOpenCompany(c)}
                style={{
                  background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12,
                  padding: '18px 20px', cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
                  position: 'relative',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                  e.currentTarget.style.borderColor = green;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = t.border;
                }}
              >
                {/* Header row: logo + name + industry */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: c.logo ? '#fff' : greenLight,
                    border: `1px solid ${t.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, overflow: 'hidden',
                  }}>
                    {c.logo
                      ? <img src={c.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      : <Building2 size={20} style={{ color: green }} />
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 700, fontFamily: sans, color: t.textPrimary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 10, fontFamily: sans, color: t.textMuted, marginTop: 2 }}>
                      {c.industry}{c.hq ? ` · ${c.hq}` : ''}
                    </div>
                  </div>
                  {/* Status indicator */}
                  <span style={{
                    fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                    padding: '3px 8px', borderRadius: 10, flexShrink: 0,
                    background: published ? greenLight : t.borderLight,
                    color: published ? green : t.textMuted,
                  }}>
                    {published ? 'Published' : 'Pending'}
                  </span>
                </div>

                {/* KPI row or empty state */}
                {published ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted, fontFamily: sans, marginBottom: 2 }}>
                          Cash
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, fontFamily: mono, color: t.textPrimary }}>
                          {fmtCompactC(c.cashCad, cc)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted, fontFamily: sans, marginBottom: 2 }}>
                          Runway
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, fontFamily: mono, color: rColor || t.textPrimary }}>
                          {c.runwayMonths} mo
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.4, color: t.textMuted, fontFamily: sans, marginBottom: 2 }}>
                          Team
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, fontFamily: mono, color: t.textPrimary }}>
                          {c.headcount}
                        </div>
                      </div>
                    </div>

                    {/* Footer: last published */}
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      paddingTop: 10, borderTop: `1px solid ${t.borderLight}`,
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, fontFamily: sans, color: t.textFaint }}>
                        <Calendar size={10} />
                        Published {formatDate(c.lastPublishedAt)}
                      </span>
                      <ArrowRight size={14} style={{ color: t.textMuted }} />
                    </div>
                  </>
                ) : (
                  <div style={{
                    padding: '16px 12px', textAlign: 'center',
                    border: `1px dashed ${t.borderLight}`, borderRadius: 8,
                  }}>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans, fontWeight: 600, marginBottom: 2 }}>
                      Data available soon
                    </div>
                    <div style={{ fontSize: 9, color: t.textFaint, fontFamily: sans }}>
                      Waiting on first published report
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div style={{
            padding: '40px 20px', textAlign: 'center',
            border: `1px dashed ${t.border}`, borderRadius: 10, marginTop: 10,
          }}>
            <div style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, fontWeight: 600 }}>
              No companies match your search
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', fontSize: 9, color: t.textFaint, fontFamily: sans, padding: '28px 0 0' }}>
          Confidential · Investor portfolio
        </div>
      </div>
    </div>
  );
}
